// Accès aux notifications (Supabase).
// - lecture : via les RPC serveur `mes_notifications` / `compter_notifications_non_lues`
//   (SECURITY DEFINER). Elles n'exposent QUE les notifications du compte connecté
//   et, pour un employé, uniquement celles des modules auxquels il a accès ;
// - marquage lu : via RPC serveur (`marquer_notification_lue` /
//   `marquer_toutes_notifications_lues`), jamais par un update direct.
//
// Note : la table `notifications` n'accorde volontairement PAS de SELECT direct
// au rôle `authenticated`. Toute lecture passe donc obligatoirement par ces
// fonctions, ce qui garantit une isolation stricte côté serveur.

import { supabase } from "@/store/shared/supabase";
import type { NotificationItem } from "./types";

interface NotificationRow {
  id: string;
  type: string;
  titre: string;
  message: string | null;
  lien: string | null;
  module: string | null;
  lu: boolean;
  created_at: string;
  lu_at: string | null;
  metadata: Record<string, unknown> | null;
  scope_type: string | null;
}

function mapRow(row: NotificationRow): NotificationItem {
  return {
    id: row.id,
    type: row.type,
    titre: row.titre,
    message: row.message,
    lien: row.lien,
    module: row.module,
    lu: row.lu,
    createdAt: row.created_at,
    luAt: row.lu_at,
    metadata: row.metadata ?? {},
    scopeType: row.scope_type,
  };
}

/** Récupère les notifications du compte connecté (les plus récentes d'abord). */
export async function fetchNotifications(limit = 40): Promise<NotificationItem[]> {
  const { data, error } = await supabase.rpc("mes_notifications", { p_limit: limit });
  if (error) throw error;
  return ((data as NotificationRow[] | null) ?? []).map(mapRow);
}

/** Compte les notifications non lues du compte connecté. */
export async function countUnreadNotifications(): Promise<number> {
  const { data, error } = await supabase.rpc("compter_notifications_non_lues");
  if (error) throw error;
  return typeof data === "number" ? data : 0;
}

/** Marque une notification comme lue (borné au compte connecté côté serveur). */
export async function markNotificationRead(id: string): Promise<void> {
  const { error } = await supabase.rpc("marquer_notification_lue", { p_id: id });
  if (error) throw error;
}

/** Marque toutes les notifications du compte connecté comme lues. */
export async function markAllNotificationsRead(): Promise<void> {
  const { error } = await supabase.rpc("marquer_toutes_notifications_lues");
  if (error) throw error;
}

/**
 * Génère les notifications d'impayés de l'agence connectée.
 *
 * - Idempotent : le serveur n'insère jamais deux fois la notification d'un
 *   même impayé (un anti-doublon garantit une seule ligne par compte et par
 *   loyer). Un impayé déjà notifié, lu ou non, n'est jamais recréé.
 * - No-op pour tout compte hors espace Agence (le serveur renvoie 0).
 * - Aucun effet sur le statut financier : c'est un simple reflet.
 */
export async function generateImpayeNotifications(): Promise<void> {
  const { error } = await supabase.rpc("generer_notifications_impayes");
  if (error) throw error;
}

/**
 * Génère les notifications de fin d'essai (offre de lancement) : paliers
 * J-30, J-7 et J-1 avant la fin de l'essai gratuit.
 *
 * - Idempotent : le serveur n'insère jamais deux fois la notification d'un même
 *   palier pour un même compte (index unique partiel). Aucun doublon, même en
 *   naviguant ou en rafraîchissant plusieurs fois.
 * - No-op dès que l'essai n'est plus actif (compte payant, gratuit standard,
 *   abonnement expiré) : le serveur renvoie 0.
 * - Simple repère d'interface : aucun effet sur l'abonnement ni la facturation.
 */
export async function generateEssaiNotifications(): Promise<void> {
  const { error } = await supabase.rpc("generer_notifications_essai");
  if (error) throw error;
}